import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap, of, catchError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        // Use static navigation data - no API call needed
        const defaultNavigation: Navigation = {
                    default: [
                        {
                            id: 'home',
                            title: 'Home',
                            type: 'basic',
                            icon: 'heroicons_outline:home',
                            link: '/home'
                        },
                        {
                            id: 'service-master',
                            title: 'Service Master',
                            type: 'basic',
                            icon: 'heroicons_outline:wrench-screwdriver',
                            link: '/service-master'
                        },
                        {
                            id: 'task-master',
                            title: 'Task Master',
                            type: 'basic',
                            icon: 'heroicons_outline:clipboard-document-list',
                            link: '/task-master'
                        },
                        {
                            id: 'vendors-documents',
                            title: 'Vendor Management',
                            type: 'basic',
                            icon: 'heroicons_outline:document-text',
                            link: '/vendors-documents'
                        },
                        {
                            id: 'coupons',
                            title: 'Coupons',
                            type: 'basic',
                            icon: 'heroicons_outline:ticket',
                            link: '/coupons'
                        },
                        {
                            id: 'vendor-service-mapping',
                            title: 'Vendor Service Mapping',
                            type: 'basic',
                            icon: 'heroicons_outline:adjustments-horizontal',
                            link: '/vendor-service-mapping'
                        }, 
                        {
                            id: 'Workforce Management',
                            title: 'Workforce',
                            type: 'basic',
                            icon: 'heroicons_outline:user-group',
                            link: '/workforce'
                        },
                        {
                            id: 'workforce-blocklist',
                            title: 'Workforce Blocklist',
                            type: 'basic',
                            icon: 'heroicons_outline:shield-exclamation',
                            link: '/workforce-blocklist'
                        },
                        {
                            id: 'blast-confirmation',
                            title: 'Blast Confirmation',
                            type: 'basic',
                            icon: 'heroicons_outline:megaphone',
                            link: '/blast-confirmation'
                        },
                        {
                            id: 'commission',
                            title: 'Commission Report',
                            type: 'basic',
                            icon: 'heroicons_outline:banknotes',
                            link: '/commission'
                        },
                        {
                            id: 'payout-management',
                            title: 'Payout Management',
                            type: 'basic',
                            icon: 'heroicons_outline:currency-rupee',
                            link: '/payout-management'
                        },
                         {
                            id: 'Ad Management',
                            title: 'Ad Management',
                            type: 'basic',
                            icon: 'heroicons_outline:megaphone',
                            link: '/ad-management'
                        },
                        {
                            id: 'app-config',
                            title: 'Application Configuration',
                            type: 'basic',
                            icon: 'heroicons_outline:cog-6-tooth',
                            link: '/app-config'
                        }
                    ],
                    compact: [
                        {
                            id: 'home',
                            title: 'Home',
                            type: 'basic',
                            icon: 'heroicons_outline:home',
                            link: '/home'
                        },
                        {
                            id: 'service-master',
                            title: 'Service Master',
                            type: 'basic',
                            icon: 'heroicons_outline:wrench-screwdriver',
                            link: '/service-master'
                        },
                        {
                            id: 'task-master',
                            title: 'Task Master',
                            type: 'basic',
                            icon: 'heroicons_outline:clipboard-document-list',
                            link: '/task-master'
                        },
                        {
                            id: 'vendors-documents',
                            title: 'Vendor Management',
                            type: 'basic',
                            icon: 'heroicons_outline:document-text',
                            link: '/vendors-documents'
                        },
                        {
                            id: 'coupons',
                            title: 'Coupons',
                            type: 'basic',
                            icon: 'heroicons_outline:ticket',
                            link: '/coupons'
                        },
                        {
                            id: 'vendor-service-mapping',
                            title: 'Vendor Service Mapping',
                            type: 'basic',
                            icon: 'heroicons_outline:adjustments-horizontal',
                            link: '/vendor-service-mapping'
                        }, 
                        {
                            id: 'Workforce Management',
                            title: 'Workforce',
                            type: 'basic',
                            icon: 'heroicons_outline:user-group',
                            link: '/workforce'
                        },
                        {
                            id: 'workforce-blocklist',
                            title: 'Workforce Blocklist',
                            type: 'basic',
                            icon: 'heroicons_outline:shield-exclamation',
                            link: '/workforce-blocklist'
                        },
                        {
                            id: 'blast-confirmation',
                            title: 'Blast Confirmation',
                            type: 'basic',
                            icon: 'heroicons_outline:megaphone',
                            link: '/blast-confirmation'
                        },
                        {
                            id: 'commission',
                            title: 'Commission Report',
                            type: 'basic',
                            icon: 'heroicons_outline:banknotes',
                            link: '/commission'
                        },
                        {
                            id: 'payout-management',
                            title: 'Payout Management',
                            type: 'basic',
                            icon: 'heroicons_outline:currency-rupee',
                            link: '/payout-management'
                        },
                         {
                            id: 'Ad Management',
                            title: 'Ad Management',
                            type: 'basic',
                            icon: 'heroicons_outline:megaphone',
                            link: '/ad-management'
                        },
                        {
                            id: 'app-config',
                            title: 'Application Configuration',
                            type: 'basic',
                            icon: 'heroicons_outline:cog-6-tooth',
                            link: '/app-config'
                        }
                    ],
                    futuristic: [
                        {
                            id: 'home',
                            title: 'Home',
                            type: 'basic',
                            icon: 'heroicons_outline:home',
                            link: '/home'
                        },
                        {
                            id: 'service-master',
                            title: 'Service Master',
                            type: 'basic',
                            icon: 'heroicons_outline:wrench-screwdriver',
                            link: '/service-master'
                        },
                        {
                            id: 'task-master',
                            title: 'Task Master',
                            type: 'basic',
                            icon: 'heroicons_outline:clipboard-document-list',
                            link: '/task-master'
                        },
                        {
                            id: 'vendors-documents',
                            title: 'Vendor Management',
                            type: 'basic',
                            icon: 'heroicons_outline:document-text',
                            link: '/vendors-documents'
                        },
                        {
                            id: 'coupons',
                            title: 'Coupons',
                            type: 'basic',
                            icon: 'heroicons_outline:ticket',
                            link: '/coupons'
                        },
                        {
                            id: 'vendor-service-mapping',
                            title: 'Vendor Service Mapping',
                            type: 'basic',
                            icon: 'heroicons_outline:adjustments-horizontal',
                            link: '/vendor-service-mapping'
                        }, 
                        {
                            id: 'Workforce Management',
                            title: 'Workforce',
                            type: 'basic',
                            icon: 'heroicons_outline:user-group',
                            link: '/workforce'
                        },
                        {
                            id: 'workforce-blocklist',
                            title: 'Workforce Blocklist',
                            type: 'basic',
                            icon: 'heroicons_outline:shield-exclamation',
                            link: '/workforce-blocklist'
                        },
                        {
                            id: 'blast-confirmation',
                            title: 'Blast Confirmation',
                            type: 'basic',
                            icon: 'heroicons_outline:megaphone',
                            link: '/blast-confirmation'
                        },
                        {
                            id: 'commission',
                            title: 'Commission Report',
                            type: 'basic',
                            icon: 'heroicons_outline:banknotes',
                            link: '/commission'
                        },
                        {
                            id: 'payout-management',
                            title: 'Payout Management',
                            type: 'basic',
                            icon: 'heroicons_outline:currency-rupee',
                            link: '/payout-management'
                        },
                         {
                            id: 'Ad Management',
                            title: 'Ad Management',
                            type: 'basic',
                            icon: 'heroicons_outline:megaphone',
                            link: '/ad-management'
                        },
                        {
                            id: 'app-config',
                            title: 'Application Configuration',
                            type: 'basic',
                            icon: 'heroicons_outline:cog-6-tooth',
                            link: '/app-config'
                        }
                    ],
                    horizontal: [
                        {
                            id: 'home',
                            title: 'Home',
                            type: 'basic',
                            icon: 'heroicons_outline:home',
                            link: '/home'
                        },
                        {
                            id: 'service-master',
                            title: 'Service Master',
                            type: 'basic',
                            icon: 'heroicons_outline:wrench-screwdriver',
                            link: '/service-master'
                        },
                        {
                            id: 'task-master',
                            title: 'Task Master',
                            type: 'basic',
                            icon: 'heroicons_outline:clipboard-document-list',
                            link: '/task-master'
                        },
                        {
                            id: 'vendors-documents',
                            title: 'Vendor Management',
                            type: 'basic',
                            icon: 'heroicons_outline:document-text',
                            link: '/vendors-documents'
                        },
                        {
                            id: 'coupons',
                            title: 'Coupons',
                            type: 'basic',
                            icon: 'heroicons_outline:ticket',
                            link: '/coupons'
                        },
                        {
                            id: 'vendor-service-mapping',
                            title: 'Vendor Service Mapping',
                            type: 'basic',
                            icon: 'heroicons_outline:adjustments-horizontal',
                            link: '/vendor-service-mapping'
                        }, 
                        {
                            id: 'Workforce Management',
                            title: 'Workforce',
                            type: 'basic',
                            icon: 'heroicons_outline:user-group',
                            link: '/workforce'
                        },
                        {
                            id: 'workforce-blocklist',
                            title: 'Workforce Blocklist',
                            type: 'basic',
                            icon: 'heroicons_outline:shield-exclamation',
                            link: '/workforce-blocklist'
                        },
                        {
                            id: 'blast-confirmation',
                            title: 'Blast Confirmation',
                            type: 'basic',
                            icon: 'heroicons_outline:megaphone',
                            link: '/blast-confirmation'
                        },
                        {
                            id: 'commission',
                            title: 'Commission Report',
                            type: 'basic',
                            icon: 'heroicons_outline:banknotes',
                            link: '/commission'
                        },
                        {
                            id: 'payout-management',
                            title: 'Payout Management',
                            type: 'basic',
                            icon: 'heroicons_outline:currency-rupee',
                            link: '/payout-management'
                        },
                        {
                            id: 'Ad Management',
                            title: 'Ad Management',
                            type: 'basic',
                            icon: 'heroicons_outline:megaphone',
                            link: '/ad-management'
                        },
                        {
                            id: 'app-config',
                            title: 'Application Configuration',
                            type: 'basic',
                            icon: 'heroicons_outline:cog-6-tooth',
                            link: '/app-config'
                        }
                    ]
                };
        this._navigation.next(defaultNavigation);
        return of(defaultNavigation);
    }
}
